import React, { PropsWithChildren } from "react";
import { StyleSheet } from "react-native";
import { useTheme } from "@/hooks/useTheme";
import Animated, {
  useAnimatedReaction,
  useAnimatedRef,
  useAnimatedScrollHandler,
  SharedValue,
} from "react-native-reanimated";
import { TABLE_START_WIDTH, TABLE_START_WIDTH_SELECTABLE } from "./TableStart";
import { TABLE_END_WIDTH } from "./TableEnd";

const Anchor = ({ children }: PropsWithChildren) => children;
Anchor.displayName = "SummaryRow.Anchor";
const Cells = ({ children }: PropsWithChildren) => children;
Cells.displayName = "SummaryRow.Cells";

export interface SummaryRowProps {
  children?: React.ReactNode;
  scrollX: SharedValue<number>;
  columnWidths: SharedValue<Record<string, number>>;
  selectable?: boolean;
}

export function SummaryRow({ children, scrollX, selectable }: SummaryRowProps) {
  const { colors } = useTheme();
  const ref = useAnimatedRef<Animated.ScrollView>();
  const all = React.Children.toArray(children);
  const anchor = all.find((c) => React.isValidElement(c) && c.type === SummaryRow.Anchor);
  const cells = all.find((c) => React.isValidElement(c) && c.type === SummaryRow.Cells);

  const scrollHandler = useAnimatedScrollHandler((event) => {
    scrollX.set(event.contentOffset.x);
  });

  useAnimatedReaction(
    () => scrollX.get(),
    (x) => {
      ref.current?.scrollTo({ x, animated: false });
    },
    [scrollX],
  );

  return (
    <Animated.View
      style={[
        styles.container,
        {
          borderColor: colors.border,
          marginLeft: (selectable ? TABLE_START_WIDTH_SELECTABLE : TABLE_START_WIDTH) - 1,
          marginRight: TABLE_END_WIDTH + 1,
        },
      ]}
    >
      <Animated.View
        style={[
          styles.anchor,
          {
            backgroundColor: colors.card,
            borderColor: colors.border,
          },
        ]}
      >
        {anchor}
      </Animated.View>
      <Animated.ScrollView
        ref={ref}
        horizontal={true}
        scrollEnabled={false}
        showsHorizontalScrollIndicator={false}
        onScroll={scrollHandler}
        scrollEventThrottle={16}
        style={[styles.row]}
      >
        {cells}
      </Animated.ScrollView>
    </Animated.View>
  );
}

SummaryRow.Anchor = Anchor;
SummaryRow.Cells = Cells;

const styles = StyleSheet.create({
  container: {
    overflow: "hidden",
    borderRadius: 6,
    borderWidth: StyleSheet.hairlineWidth,
    zIndex: 1,
    elevation: 2,
    flexDirection: "row",
    height: 38,
  },
  anchor: {
    borderTopLeftRadius: 6,
    borderBottomLeftRadius: 6,
    minHeight: 38,
    flexDirection: "row",
  },
  row: {
    flex: 1,
    minHeight: 38,
    flexDirection: "row",
  },
});
